import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import VerificationCard from "@/components/VerificationCard";

function RelationList({ title, items, kind, onUndo }) {
  return (
    <div>
      <div className="text-sm font-semibold mb-2">{title} <span className="text-muted-foreground font-normal">({items.length})</span></div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">No one here.</p>
      ) : (
        <div className="space-y-2">
          {items.map((u) => (
            <div key={u.user_id} data-testid={`${kind}-row-${u.user_id}`} className="flex items-center gap-3">
              <Avatar user={u} size={34} />
              <span className="text-sm flex-1 truncate">{u.name}</span>
              <Button data-testid={`un${kind}-${u.user_id}`} size="sm" variant="outline" className="rounded-xl" onClick={() => onUndo(kind, u)}>
                {kind === "block" ? "Unblock" : "Unmute"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { data = { blocked: [], muted: [] }, refetch } = useQuery({
    queryKey: ["relations"],
    queryFn: async () => (await api.get("/safety/relations")).data,
  });

  const undo = async (kind, u) => {
    await api.delete(`/users/${u.user_id}/${kind}`);
    toast.success(`${kind === "block" ? "Unblocked" : "Unmuted"} ${u.name}`);
    refetch();
  };

  return (
    <div data-testid="settings-page" className="space-y-6">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold">Settings</h1>
      <VerificationCard />
      <section className="bg-card border border-border rounded-2xl p-5 space-y-5">
        <div>
          <h2 className="font-heading text-lg font-bold">Privacy & safety</h2>
          <p className="text-xs text-muted-foreground mt-1">Blocked people can't see your posts, message, call or friend you. Muted people's posts and stories are hidden from your feed — they won't know.</p>
        </div>
        <RelationList title="Blocked" items={data.blocked} kind="block" onUndo={undo} />
        <RelationList title="Muted" items={data.muted} kind="mute" onUndo={undo} />
      </section>
      <button data-testid="settings-guidelines-link" onClick={() => navigate("/guidelines")} className="w-full flex items-center gap-3 bg-card border border-border rounded-2xl p-5 hover:border-[#6C0BA9]/50 transition-colors text-left">
        <ShieldCheck className="w-5 h-5 text-[#C084FC]" />
        <span className="flex-1 text-sm font-semibold">Community Guidelines</span>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </button>
    </div>
  );
}
